import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import { RichTreeView, type TreeViewDefaultItemModelProperties } from '@mui/x-tree-view';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import type { Perfil } from '../../api-client/perfiles/perfil';
import type {
  ActualizarPerfilRequest,
  RegistrarPerfilRequest
} from '../../api-client/perfiles/perfiles.api';
import type { Modulo } from '../../api-client/permisos/modulo';
import { usePerfilesStore } from '../../stores/perfiles/perfiles.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import AppTextField from '../ui/AppTextField';
import EditForm from '../ui/EditForm';

const validationSchema = yup.object({
  id: yup.string().required('La clave es requerida'),
  nombre: yup.string().required('El nombre es requerido'),
  permisos: yup.array().min(1, 'Al menos un permiso es requerido')
});

const mapeaModulosPermisos = (modulos: Modulo[]): TreeViewDefaultItemModelProperties[] => [
  {
    id: 'root',
    label: 'Todos los Permisos',
    children: modulos.map((x) => ({
      id: x.id,
      label: x.descripcion,
      children: x.permisos.map((y) => ({ id: `${x.id}|${y.id}`, label: y.descripcion }))
    }))
  }
];

type PerfilFormProps = {
  perfil?: Perfil;
  onSave:
    | ((values: ActualizarPerfilRequest) => Promise<void>)
    | ((values: RegistrarPerfilRequest) => Promise<boolean>);
} & CommonEntityFormProps;

const PerfilForm = ({ perfil, esAgregar, onSave, onDelete, onActivate }: PerfilFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || perfil?.activo === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore();
  const { modulos, cargarPermisosDisponibles } = usePerfilesStore();
  const permisos = (perfil?.permisos ?? []).map((x) => `${x.idModulo}|${x.idPermiso}`);
  const initialValues = {
    id: perfil?.id ?? 0,
    nombre: perfil?.nombre ?? '',
    permisos: permisos
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const data = {
        ...values,
        permisos: values.permisos.map((x) => {
          const [idModulo, idPermiso] = x.split('|');
          return { idModulo, idPermiso };
        })
      };
      const success = await onSave(data);
      if (success) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  useEffect(() => {
    cargarPermisosDisponibles();
  }, []);

  const reset = () => formik.resetForm({ values: initialValues });

  const handleSelectedItemsChange = async (_: React.SyntheticEvent | null, ids: string[]) => {
    await formik.setFieldValue(
      'permisos',
      ids.filter((id) => id.includes('|'))
    );
  };

  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="center" spacing={6}>
      <EditForm
        module="perfiles"
        title="Perfiles"
        readonly={!activo}
        returnLink="/perfiles"
        loading={loading}
        showActivate={false}
        showDelete={esEditar}
        onSave={formik.handleSubmit}
        onClear={reset}
        onDelete={onDelete}
        onActivate={onActivate}
      >
        <Grid>
          <Grid container spacing={2}>
            <Grid>
              <AppTextField
                name="id"
                label="Clave"
                placeholder="Clave"
                value={formik.values.id}
                readOnly={true}
                disabled={loading}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid size={12}>
              <AppTextField
                name="nombre"
                label="Nombre"
                placeholder="Nombre"
                maxLength={80}
                disabled={loading || inactivo}
                value={formik.values.nombre}
                onChange={formik.handleChange}
                error={formik.touched.nombre && Boolean(formik.errors.nombre)}
                helperText={getLengthOrErrorText(
                  formik.touched.nombre ? formik.errors.nombre : '',
                  formik.values.nombre.length,
                  80
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid>
              <AppTextField label="Status" value={status} disabled={true} />
            </Grid>
          </Grid>
        </Grid>
      </EditForm>
      <Paper variant="outlined" sx={{ padding: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Permisos disponibles</Typography>
        </Box>
        <Box sx={{ minHeight: '15vh', maxHeight: '50vh', overflowY: 'auto' }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            items={mapeaModulosPermisos(modulos)}
            selectionPropagation={{
              parents: true,
              descendants: true
            }}
            selectedItems={formik.values.permisos}
            onSelectedItemsChange={handleSelectedItemsChange}
          />
        </Box>
      </Paper>
    </Stack>
  );
};

export default PerfilForm;
